import type {AnalyticsContext} from '~/types/analytics';
import type {ShopActionType} from '~/types/flow';
import type {OAuthParams} from '~/types/oauth';
import type {PopupWindowParams} from '~/types/popup';

interface BuildAuthorizeUrlParamsBase {
  /**
   * Defines the context in which this authorize flow is used (such as `loginWithShopClassicCustomerAccounts`
   * or `loginWithShopSelfServe`) to differentiate events when the same flow is used in different contexts.
   */
  analyticsContext?: AnalyticsContext;
  /** Links analytics events together in one flow. */
  analyticsTraceId?: string;
  /** The app's api key. */
  apiKey?: string;
  /** If `true` the authorize URL will not get redirected to pay.shopify.com. */
  avoidPayAltDomain?: boolean;
  /** If `true` the authorize URL will go straight to `sdk-authorize` rather than bouncing through `sdk-session` */
  avoidSdkSession?: boolean;
  /** Optional token for the checkout if the component is used at checkout. */
  checkoutToken?: string;
  /** Checkout version (checkout_one or classic) for analytics. */
  checkoutVersion?: string;
  /**
   * If `true`, Pay will present the user with the personalization consent challenge, if the feature is on
   * and the user hasn't previously consented.
   */
  consentChallenge?: boolean;
  /**
   * If `true` and Pay does not find a user with the given email address, it will not show the Sign Up flow.
   * Use when doing headless user lookups.
   */
  disableSignUp?: boolean;
  /** If `true` Pay will only respond with email verified users. Otherwise Pay will respond with all users. */
  emailVerificationRequired?: boolean;
  /** Set to true to show the full login form, false to show the login button only. */
  isFullView?: boolean;
  /** The flow to use for the login. */
  flow?: ShopActionType;
  flowVersion?: string;
  /** If `true`, Pay will verify the client is permitted to customize the modal. */
  modalCustomized?: boolean;
  /** If `true`, Pay will not render the 1-tap flow cookied users */
  requireVerification?: boolean;
  /** Shop ID */
  shopId?: number;
  /** Optional transaction params if the component is used at checkout. */
  transactionParams?: string;
}

export interface BuildPayAuthorizeUrlParams
  extends BuildAuthorizeUrlParamsBase {
  /**
   * The OAuth params for OAuth flows (such as code or implicit flow).
   * Note that for the implicit flow, the only required attribute in is `clientId`.
   */
  oauthParams?: OAuthParams;
  /** The params to configure a popup window when redirect_type is pop_up. */
  popupWindowParams?: PopupWindowParams;
}

interface BuildCoreAuthorizeUrlParams extends BuildAuthorizeUrlParamsBase {
  oauthParams?: never;
  popupWindowParams?: never;
}

export type BuildAuthorizeUrlParams = (
  | BuildCoreAuthorizeUrlParams
  | BuildPayAuthorizeUrlParams
) & {
  /** The implementation version - `1` or `2` */
  version: string;
};

export const buildAuthorizeUrl = (props: BuildAuthorizeUrlParams): string => {
  if (props.version === '1') {
    if (!props.oauthParams) return '';

    const params = buildUrlSearchParams(props);

    /**
     * When `avoidPayAltDomain` is false, we need to route requests through the shop.app/pay/sdk-session endpoint.
     * The session endpoint will allow us to check for the presence of a Pay user session  and hoist it to the
     * "alt" pay.shopify.com/pay/sdk-authorize endpoint.
     */
    const path = props.avoidPayAltDomain
      ? '/pay/sdk-authorize'
      : '/pay/sdk-session';
    return `${PAY_AUTH_DOMAIN}${path}?${params}`;
  }

  const params = buildUrlSearchParams(props);

  return `${CORE_AUTH_DOMAIN}/services/login_with_shop/authorize?${params}`;
};

export const booleanQueryParam = (
  value?: boolean,
): undefined | 'true' | 'false' => {
  if (value === undefined) {
    return undefined;
  }

  if (value === false) {
    return 'false';
  }

  return 'true';
};

const buildUrlSearchParams = ({
  analyticsTraceId,
  analyticsContext,
  apiKey,
  avoidSdkSession,
  checkoutToken,
  checkoutVersion,
  consentChallenge,
  disableSignUp,
  emailVerificationRequired,
  flow,
  flowVersion,
  isFullView,
  modalCustomized,
  oauthParams,
  popupWindowParams,
  requireVerification,
  transactionParams,
  version,
}: BuildAuthorizeUrlParams) => {
  const signUpEnabled =
    disableSignUp === undefined ? undefined : disableSignUp === false;

  /* eslint-disable @typescript-eslint/naming-convention */
  const baseParams: Record<string, string | undefined> = {
    analytics_context: analyticsContext,
    analytics_trace_id: analyticsTraceId,
    avoid_sdk_session: booleanQueryParam(avoidSdkSession),
    compact_layout: 'true',
    api_key: apiKey,
    checkout_token: checkoutToken,
    checkout_version: checkoutVersion,
    consent_challenge: booleanQueryParam(consentChallenge),
    'customize-modal': booleanQueryParam(modalCustomized),
    email_verification_required: booleanQueryParam(emailVerificationRequired),
    flow: flow ? flow.toString() : undefined,
    flow_version: flowVersion,
    full_view: booleanQueryParam(isFullView),
    // eslint-disable-next-line no-warning-comments
    // TODO: Revisit this in the future as this is temporarily using `en`.
    // BUILD_LOCALE is used for generating localized bundles.
    // See ./scripts/i18n-dynamic-import-replacer-rollup.mjs for more info.
    locale: 'en',
    require_verification: booleanQueryParam(requireVerification),
    sign_up_enabled: booleanQueryParam(signUpEnabled),
    target_origin: window.location.origin,
    transaction_params: transactionParams,
  };
  /* eslint-enable @typescript-eslint/naming-convention */

  const {
    clientId,
    codeChallenge,
    codeChallengeMethod,
    redirectType,
    redirectUri = window.location.origin,
    responseMode = 'web_message',
    responseType = 'id_token',
    scope = 'openid email',
    state,
  } = oauthParams || {};
  const {popUpName, popUpFeatures} = popupWindowParams || {};

  baseParams.client_id = clientId;
  baseParams.code_challenge = codeChallenge;
  baseParams.code_challenge_method = codeChallengeMethod;

  baseParams.pop_up_features =
    redirectType === 'pop_up' ? popUpFeatures : undefined;
  baseParams.pop_up_name = redirectType === 'pop_up' ? popUpName : undefined;
  baseParams.redirect_type = redirectType;
  baseParams.redirect_uri = redirectUri;
  baseParams.response_mode = responseMode;

  baseParams.response_type = responseType;
  baseParams.scope = scope;
  baseParams.state = state;
  baseParams.version = version;

  Object.keys(baseParams).forEach(
    (key) => baseParams[key] === undefined && delete baseParams[key],
  );

  return new URLSearchParams(baseParams as Record<string, string>);
};

export const extractSpinUrlSuffix = (): string | null => {
  // eslint-disable-next-line no-process-env
  const onSpin = process.env.NODE_ENV === 'spin';
  if (onSpin) {
    const spinSuffix = new URLSearchParams(window.location.search).get('spin');
    if (spinSuffix) return spinSuffix;
  }

  const found = window.location.origin.match(/\.(?:(?:\w|-)+\.){3}spin\.dev$/);
  return found ? found[0] : null;
};

const getCoreAuthDomain = (): string => {
  // Gets set at build time by rollup-replace
  // eslint-disable-next-line no-process-env
  const nodeEnv = process.env.NODE_ENV;

  switch (nodeEnv) {
    case 'development':
      return 'https://shop1.myshopify.io';
    case 'spin':
      return `https://shop1.shopify${extractSpinUrlSuffix()}`;
    case 'staging':
    case 'production':
    default:
      return window.location.origin;
  }
};

const getPayAuthDomain = (): string => {
  // Gets set at build time by rollup-replace
  // eslint-disable-next-line no-process-env
  switch (process.env.NODE_ENV) {
    case 'development':
      return 'https://shopapp.myshopify.io';
    case 'spin':
      return `https://shop-server${extractSpinUrlSuffix()}`;
    case 'staging':
      return 'https://pay-staging2.shopifycloud.com';
    case 'production':
    default:
      return 'https://shop.app';
  }
};

const getPayAuthDomainAlt = (): string => {
  // Gets set at build time by rollup-replace
  // eslint-disable-next-line no-process-env
  switch (process.env.NODE_ENV) {
    case 'development':
      return 'https://pay.myshopify.io';
    case 'spin':
      return `https://pay-shopify-com${extractSpinUrlSuffix()}`;
    case 'staging':
      return 'https://pay-staging2.shopifycloud.com';
    case 'production':
    default:
      return 'https://pay.shopify.com';
  }
};

export const CORE_AUTH_DOMAIN = getCoreAuthDomain();
export const PAY_AUTH_DOMAIN = getPayAuthDomain();
export const PAY_AUTH_DOMAIN_ALT = getPayAuthDomainAlt();
